import {
  type AmountDto,
  CurrencyCode as ApiCurrencyCode,
  Region,
} from "Infrastructure/Api/Api";
import { CurrencyCode } from "Models/CurrencyCodes";
import BigNumber from "bignumber.js";

const satoshiConstant = new BigNumber(100000000);

export const satoshiToBitcoin = (value: number) =>
  new BigNumber(value).dividedBy(satoshiConstant).toNumber();

export const bitcoinToSatoshi = (value: number) => {
  return new BigNumber(value)
    .multipliedBy(satoshiConstant)
    .integerValue()
    .toNumber();
};

export const formatCurrency = ({
  amount,
  currencyCode,
  maxDecimalPlaces = 2,
  minDecimalPlaces = 0,
  asBitcoin = false,
  isNumberOnly = false,
}: {
  amount?: number | null;
  currencyCode?: CurrencyCode | string | null;
  maxDecimalPlaces?: number;
  minDecimalPlaces?: number;
  asBitcoin?: boolean;
  isNumberOnly?: boolean;
}) => {
  const locales = () => {
    switch (currencyCode) {
      case CurrencyCode.EUR:
        return "sk-SK";
      case CurrencyCode.CHF:
        return "fr-CH";
      case CurrencyCode.GBP:
        return "en-GB";
      case CurrencyCode.USD:
        return "en-US";
      case CurrencyCode.PLN:
        return "pl-PL";

      default:
        return "cs-CZ";
    }
  };

  if (currencyCode === CurrencyCode.BTC) {
    if (asBitcoin) {
      return new Intl.NumberFormat(locales(), {
        style: "currency",
        currency: "BTC",
        maximumFractionDigits: 8,
        minimumFractionDigits: 8,
      }).format(amount ?? 0);
    }

    return new Intl.NumberFormat(locales(), {
      style: "currency",
      currency: "SAT",
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(bitcoinToSatoshi(amount ?? 0));
  }

  // Convert very small negative numbers to 0
  const normalizedAmount = amount && Math.abs(amount) < 0.000001 ? 0 : amount;

  // No currency -> plain number, visibly missing its symbol. Money must never fall
  // back to a guessed currency; a bare number is an honest bug signal, a silent
  // "Kč" is a lie that hides the missing data.
  const formatter = new Intl.NumberFormat(locales(), {
    style: isNumberOnly || !currencyCode ? "decimal" : "currency",
    ...(currencyCode ? { currency: currencyCode } : {}),
    maximumFractionDigits: maxDecimalPlaces,
    minimumFractionDigits: minDecimalPlaces,
  });
  return formatter.format(normalizedAmount ?? 0);
};

/**
 * Formats an API AmountDto (value + its own currency) — the standard shape for money on the API.
 * Never pairs the value with a guessed currency: a missing dto renders as a plain number.
 */
export const formatAmount = (amount?: AmountDto | null) =>
  formatCurrency({ amount: amount?.value, currencyCode: amount?.currencyCode });

// The single source of truth for "which currency does a market use". Keyed to the
// API enum because API payloads are the primary consumer (useAmount); the UI twin
// below derives from it, so adding a market forces the compiler to demand both rows.
export const REGION_TO_MARKET_CURRENCY = {
  [Region.CZ]: ApiCurrencyCode.CZK,
  [Region.SK]: ApiCurrencyCode.EUR,
} as const satisfies Record<Region, ApiCurrencyCode>;

type MarketCurrencyCode = (typeof REGION_TO_MARKET_CURRENCY)[Region];

const MARKET_CURRENCY_TO_UI: Record<MarketCurrencyCode, CurrencyCode> = {
  [ApiCurrencyCode.CZK]: CurrencyCode.CZK,
  [ApiCurrencyCode.EUR]: CurrencyCode.EUR,
};

export const getRegionCurrency = (region: Region): CurrencyCode =>
  MARKET_CURRENCY_TO_UI[REGION_TO_MARKET_CURRENCY[region]];

const API_CURRENCY_CODES: ReadonlyArray<string> =
  Object.values(ApiCurrencyCode);

/**
 * Narrows a free-form currency string (a bank account's `currency`, an attachment's
 * `currencyCode`) to the API enum, so a money payload is never built from a code the API does
 * not know.
 */
export const isApiCurrencyCode = (value: string): value is ApiCurrencyCode =>
  API_CURRENCY_CODES.includes(value);

/**
 * The currency's name in the given UI language ("Česká koruna", "Euro"), from the browser's own
 * locale data — no dictionary keys to maintain. Falls back to the code where Intl has no name.
 */
export const getCurrencyDisplayName = (currency: string, language: string) => {
  try {
    const name = new Intl.DisplayNames([language], { type: "currency" }).of(
      currency,
    );
    if (!name) {
      return currency;
    }
    // Czech/Slovak locale data returns lower-case names ("česká koruna"); the sheet shows them as labels.
    return name.charAt(0).toLocaleUpperCase(language) + name.slice(1);
  } catch {
    return currency;
  }
};

export const getCurrencySymbol = (currency: string) =>
  (0)
    .toLocaleString("cs-CZ", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, "")
    .trim();

type PrecisionConfig = {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
};

export const formatNumberOnly = (
  sum: number,
  {
    maximumFractionDigits = 0,
    minimumFractionDigits = 0,
  }: PrecisionConfig = {},
) =>
  new Intl.NumberFormat("cs-CZ", {
    maximumFractionDigits,
    minimumFractionDigits,
  }).format(sum ?? 0);
